import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import AdminTopBar from "./AdminTopBar";
import api from "./api/axios";
import { useActivityLogger } from "./hooks/useActivity";
import { getImageUrl } from "./utils/image";
export default function AdminEditUser() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { logAdminActivity } = useActivityLogger();
  const [adminId, setAdminId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [form, setForm] = useState({
    nama_ktp: "",
    username: "",
    email: "",
    nomor_hp: "",
    password: "",
    confirmPassword: "",
    nik: "",
    nama_samaran: "",
  });

  useEffect(() => {
    async function fetchAdmin() {
      try {
        const res = await api.get(`/admins/${username}`);
        const data = res.data;
        setAdminId(data.ID); // Save the admin's ID
        setForm({
          nama_ktp: data.full_name || data.nama_ktp || "",
          username: data.username || "",
          email: data.email || "",
          nomor_hp: data.phone || data.nomor_hp || "",
          password: "",
          confirmPassword: "",
          nik: data.nik || "",
          nama_samaran: data.pseudonym || data.nama_samaran || "",
        });

        // Set image preview if admin has profile image
        if (data.image_url) {
          setImagePreview(getImageUrl(data.image_url));
        }

        logAdminActivity(
          "view",
          "view_admin_edit_user",
          "user",
          data.ID,
          data.username,
          "success",
          "",
          `Admin mengedit data pengguna ${data.username}`
        );
      } catch (err) {
        alert("Gagal memuat data admin.");
        console.error(err.response?.data?.error || err.message);
      }
    }
    fetchAdmin();
  }, [username]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validasi ukuran file (4MB = 4 * 1024 * 1024 bytes)
      if (file.size > 5 * 1024 * 1024) {
        alert(
          "Ukuran gambar melebihi 4MB. Silakan unggah gambar yang lebih kecil."
        );
        e.target.value = null;
        return;
      }

      // Validasi tipe file
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/gif",
      ];
      if (!allowedTypes.includes(file.type)) {
        alert(
          "Format file tidak didukung. Silakan gunakan format JPG, PNG, atau GIF."
        );
        e.target.value = null;
        return;
      }

      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.password && form.password !== form.confirmPassword) {
      alert("Password dan ulangi password tidak cocok.");
      return;
    }

    // Menggunakan FormData untuk mengirim file
    const formData = new FormData();

    // Append data dari form state
    if (form.email) formData.append("email", form.email);
    if (form.nama_ktp) formData.append("nama_ktp", form.nama_ktp);
    if (form.username) formData.append("username", form.username);
    if (form.nomor_hp) formData.append("nomor_hp", form.nomor_hp);
    if (form.nama_samaran) formData.append("nama_samaran", form.nama_samaran);
    if (form.nik) formData.append("nik", form.nik);
    if (form.password) formData.append("password", form.password);

    // Append image file jika ada
    if (imageFile) {
      formData.append("image", imageFile);
    }

    try {
      await api.put(`/admins/${adminId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      logAdminActivity(
        "update",
        "update_admin_user",
        "user",
        adminId,
        form.username,
        "success",
        "",
        `Admin ${form.username} berhasil diperbarui`
      );

      alert(`Admin ${form.username} berhasil diperbarui.`);
      navigate("/admin/adminlist");
    } catch (err) {
      console.error("Error updating admin:", err);

      let errorMessage = "Gagal memperbarui admin.";

      if (err.response?.data?.error) {
        const apiError = err.response.data.error;
        if (apiError === "username already exists") {
          errorMessage =
            "Username sudah terdaftar. Silakan gunakan username lain.";
        } else if (apiError === "email already exists") {
          errorMessage = "Email sudah terdaftar. Silakan gunakan email lain.";
        } else if (apiError === "NIK already exists") {
          errorMessage = "NIK sudah terdaftar. Silakan gunakan NIK lain.";
        } else if (apiError === "phone number already exists") {
          errorMessage =
            "Nomor HP sudah terdaftar. Silakan gunakan nomor lain.";
        } else {
          errorMessage = `Update gagal: ${apiError}`;
        }
      }

      alert(errorMessage);
    }
  };

  return (
    <div className="min-h-screen flex">
      <AdminSidebar />
      <div className="flex-1 p-6 bg-gray-50">
        <AdminTopBar />
        <div className="bg-white p-6 rounded shadow">
          <button
            onClick={() => navigate(-1)}
            className="text-xl text-black font-semibold mb-8"
          >
            &lt; Edit Admin
          </button>

          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <div>
              <label className="text-sm block mb-1">Nama *</label>
              <input
                name="nama_ktp"
                value={form.nama_ktp}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="Nama lengkap"
                required
              />
            </div>

            <div>
              <label className="text-sm block mb-1">Username *</label>
              <input
                name="username"
                value={form.username}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="Username"
                required
              />
            </div>

            <div>
              <label className="text-sm block mb-1">E-mail</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="Email"
              />
            </div>

            <div>
              <label className="text-sm block mb-1">NIK (16 digit)</label>
              <input
                name="nik"
                value={form.nik}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="NIK 16 digit"
                maxLength={16}
              />
            </div>

            <div>
              <label className="text-sm block mb-1">No. HP</label>
              <input
                name="nomor_hp"
                value={form.nomor_hp}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="Nomor HP"
              />
            </div>

            <div>
              <label className="text-sm block mb-1">Nama Samaran</label>
              <input
                name="nama_samaran"
                value={form.nama_samaran}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="Nama samaran"
              />
            </div>

            <div>
              <label className="text-sm block mb-1">
                Password Baru (Opsional)
              </label>
              <input
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="Password baru"
              />
            </div>

            <div>
              <label className="text-sm block mb-1">Ulangi Password</label>
              <input
                name="confirmPassword"
                type="password"
                value={form.confirmPassword}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="Ulangi password"
              />
            </div>

            {/* Image Upload Section */}
            <div className="md:col-span-2">
              <label className="text-sm block mb-1">
                Upload Gambar Profil (opsional, max file size 4 MB)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full text-sm mb-2"
              />
              {imagePreview && (
                <div className="mt-2">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-h-40 rounded border"
                  />
                </div>
              )}
            </div>

            <div className="md:col-span-2 flex justify-end">
              <button
                type="submit"
                className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600"
              >
                Simpan Perubahan
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
